import type { UploadProgressMessage } from 'interfaces/app';

export type UploadProgressDialogProps = {
	photos: string[];
	progressPercentage: number;
	standalone: boolean;
	submittedCount: number;
};

export type ProgressDialogContext = {
	isUploadOnly: boolean;
	isFromExternalUpload: boolean;
	isDialogOpen: boolean;
	/**
	 * Whether the user previously dismissed this external upload's progress
	 * dialog manually. Reset to `false` only when a fresh external upload
	 * session starts (e.g. {@link ExternalUploadController.showQRCode}).
	 */
	wasDismissed: boolean;
};

function sanitizeProgressPercentage(value: number): number {
	if (!Number.isFinite(value)) {
		return 0;
	}

	return Math.min(
		100,
		Math.max(
			0,
			value,
		),
	);
}

function sanitizeSubmittedCount(value: number): number {
	if (!Number.isFinite(value)) {
		return 0;
	}

	return Math.max(
		0,
		value,
	);
}

function requireExternalUploadId(
	value: string | number | null | undefined,
	label: string,
): string | number {
	if (value === null || value === undefined) {
		throw new Error(`Missing ${label} id for external upload channel`);
	}

	return value;
}

/**
 * Returns true when the progress dialog should be opened for this tick. The
 * dialog only ever opens for uploadOnly or external-source progress, when
 * there are submitted items still in flight (`progressPercentage < 100`), and
 * when the user has not manually dismissed the dialog already.
 */
export function shouldOpenProgressDialog(
	data: UploadProgressMessage,
	context: ProgressDialogContext,
): boolean {
	const progressPercentage = sanitizeProgressPercentage(data.progressPercentage);
	const submittedCount = sanitizeSubmittedCount(data.submittedCount);

	if (context.isDialogOpen) {
		return false;
	}

	if (context.wasDismissed) {
		return false;
	}

	if (!submittedCount) {
		return false;
	}

	if (progressPercentage >= 100) {
		return false;
	}

	return context.isUploadOnly || context.isFromExternalUpload;
}

/**
 * Apply a progress tick to the live reactive props in place. The returned
 * tuple describes which fields actually changed — useful for tests asserting
 * minimal mutation. Photo url comparisons are positional + identity to keep
 * Vue's array-reactivity stable when nothing has actually changed.
 */
export function applyProgressUpdate(
	props: UploadProgressDialogProps,
	data: UploadProgressMessage,
): {
	progressPercentageChanged: boolean;
	submittedCountChanged: boolean;
	photosChanged: boolean;
} {
	let progressPercentageChanged = false;
	let submittedCountChanged = false;
	let photosChanged = false;

	if (
		typeof data.progressPercentage === 'number'
	) {
		const progressPercentage = sanitizeProgressPercentage(data.progressPercentage);

		if (progressPercentage !== props.progressPercentage) {
			props.progressPercentage = progressPercentage;
			progressPercentageChanged = true;
		}
	}

	if (
		typeof data.submittedCount === 'number'
	) {
		const submittedCount = sanitizeSubmittedCount(data.submittedCount);

		if (submittedCount !== props.submittedCount) {
			props.submittedCount = submittedCount;
			submittedCountChanged = true;
		}
	}

	if (data.photos) {
		const photoUrls = data.photos
			.map((photo) => photo.url)
			.filter((url): url is string => !!url);
		const same = (
			photoUrls.length === props.photos.length
			&& photoUrls.every((url, index) => url === props.photos[index])
		);

		if (!same) {
			props.photos = photoUrls;
			photosChanged = true;
		}
	}

	return {
		progressPercentageChanged,
		submittedCountChanged,
		photosChanged,
	};
}

export function buildExternalUploadMutation(
	channel: string,
	url: string,
	event: 'upload' | 'complete' | 'abort' | 'close',
	payload: UploadProgressMessage | Record<string, never>,
): {
	type: 'external_upload';
	channel: string;
	url: string;
	event: typeof event;
	payload: typeof payload;
} {
	return {
		type: 'external_upload',
		channel,
		url,
		event,
		payload,
	};
}

export function buildExternalUploadChannel(
	productId: string | number,
	userId: string | number,
): string {
	const safeProductId = requireExternalUploadId(
		productId,
		'product',
	);
	const safeUserId = requireExternalUploadId(
		userId,
		'user',
	);

	return `product/${safeProductId}/external_upload/${safeUserId}`;
}
